import {
  Home,
  Shield,
  Lightbulb,
  BarChart3,
  ClipboardList,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  Building2,
  Store,
  HelpCircle,
  MessageSquarePlus,
  Sparkles,
  Bot,
  Library,
  X,
} from "lucide-react";
import { useEffect, useState, type ComponentType, type MouseEvent } from "react";
import { NavLink } from "@/components/NavLink";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import misiaLogo from "@/assets/misia-logo-new.png";
import { type FeatureKey } from "@/config/features";
import { PERMISSIONS } from "@/config/permissions";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { SidebarUserWidget } from "./AppUserControls";

type NavItem = {
  title: string;
  url: string;
  icon: ComponentType<{ className?: string }>;
  featureKey?: FeatureKey;
  permission?: string;
  hiddenWhenPermission?: string;
  activePrefix?: string;
};

const allMainNavItems: NavItem[] = [
  { title: "Home", url: "/home", icon: Home, featureKey: "home" },
  { title: "Misia Intelligence", url: "/ai-workspace", icon: Bot, featureKey: "ai_workspace" },
  { title: "Knowledge Base", url: "/knowledge-base", icon: Library, permission: PERMISSIONS.KNOWLEDGE_READ },
  {
    title: "Cost & Analytics",
    url: "/cost-management/overview",
    icon: BarChart3,
    featureKey: "cost_management",
    permission: PERMISSIONS.COST_MANAGEMENT_READ,
    activePrefix: "/cost-management",
  },
  {
    title: "My Actions",
    url: "/cost-management/actions?view=actions",
    icon: ClipboardList,
    featureKey: "cost_management",
    hiddenWhenPermission: PERMISSIONS.COST_MANAGEMENT_READ,
    activePrefix: "/cost-management/actions",
  },
  { title: "Governance", url: "/governance", icon: Shield, featureKey: "governance" },
  { title: "Initiatives & Ideas", url: "/initiatives", icon: Lightbulb, featureKey: "initiatives" },
  {
    title: "Vendors",
    url: "/vendors",
    icon: Building2,
    featureKey: "vendors",
    permission: PERMISSIONS.VENDORS_READ,
  },
  { title: "Marketplace", url: "/marketplace", icon: Store, featureKey: "marketplace" },
];

const allSettingsItems: NavItem[] = [
  { title: "Guide", url: "/guide", icon: BookOpen, featureKey: "guide" },
  { title: "Support", url: "/support", icon: HelpCircle, featureKey: "support" },
];

export function AppSidebar() {
  const { state, toggleSidebar, isMobile } = useSidebar();
  const { isAdmin, hasAnyCapability, isFeatureEnabled } = useAuth();
  const location = useLocation();
  const [isInitiativesNavOpen, setIsInitiativesNavOpen] = useState(false);

  const canAccessInitiatives =
    isFeatureEnabled("initiatives") &&
    hasAnyCapability(PERMISSIONS.INITIATIVES_READ);
  const canAccessIdeas =
    isFeatureEnabled("ideas") &&
    hasAnyCapability(PERMISSIONS.IDEAS_READ);
  const canAccessAILeads =
    isFeatureEnabled("ai_leads") &&
    hasAnyCapability(PERMISSIONS.AI_LEADS_READ);

  // Filter items based on dynamic feature flags from plan
  const mainNavItems = allMainNavItems.filter(
    (item) =>
      item.url === "/initiatives"
        ? canAccessInitiatives || canAccessIdeas || canAccessAILeads
        : (!item.featureKey || isFeatureEnabled(item.featureKey)) &&
          (!item.permission || hasAnyCapability(item.permission)) &&
          (!item.hiddenWhenPermission ||
            !hasAnyCapability(item.hiddenWhenPermission))
  );
  const settingsItems = allSettingsItems.filter(
    (item) => !item.featureKey || isFeatureEnabled(item.featureKey)
  );
  const canAccessAdmin = isAdmin
    || hasAnyCapability('members:read')
    || hasAnyCapability('invites:read')
    || hasAnyCapability('invites:create')
    || hasAnyCapability('access_requests:manage')
    || hasAnyCapability('roles:read')
    || hasAnyCapability('org_units:read')
    || hasAnyCapability('settings:read')
    || hasAnyCapability('policy:read')
    || hasAnyCapability('policy:configure')
    || hasAnyCapability('audit_log_entry:read')
    || hasAnyCapability('integrations:manage')
    || hasAnyCapability('idp:configure')
    // Cost Analytics rights alone are enough: the Currency card is theirs
    // (rates and per-tenant accounting overrides), and without this a billing
    // administrator has the page but no way to navigate to it.
    || hasAnyCapability('cost_management:read')
    || hasAnyCapability('cost_management:manage');
  const isCollapsed = state === "collapsed";
  const isInitiativesWorkspace =
    location.pathname.startsWith("/initiatives") ||
    location.pathname.startsWith("/use-cases") ||
    location.pathname.startsWith("/ideas") ||
    location.pathname.startsWith("/ai-leads");
  const initiativesDefaultUrl = canAccessIdeas
    ? "/ideas"
    : canAccessInitiatives
      ? "/initiatives"
      : "/ai-leads";

  useEffect(() => {
    setIsInitiativesNavOpen(isInitiativesWorkspace);
  }, [isInitiativesWorkspace]);

  const handleInitiativesNavClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (isInitiativesWorkspace) event.preventDefault();
    setIsInitiativesNavOpen((open) => (isInitiativesWorkspace ? !open : true));
  };

  return (
    <Sidebar className="border-r-0" collapsible="icon">
      <SidebarHeader className={isCollapsed && !isMobile ? "p-2" : "p-4"}>
    <div
      className={
        isCollapsed && !isMobile
          ? "flex flex-col items-center gap-2"
          : "flex items-center justify-between gap-3"
      }
    >
      <Link
        to="/home"
        className={
          isCollapsed && !isMobile
            ? "flex items-center justify-center hover:opacity-80 transition-opacity"
            : "flex min-w-0 items-center gap-3 hover:opacity-80 transition-opacity"
        }
      >
        <img
          src={misiaLogo}
          alt="misia.ai logo"
          className="h-10 w-10 rounded-xl object-contain flex-shrink-0"
        />

        {(!isCollapsed || isMobile) && (
          <span className="truncate text-lg font-semibold text-sidebar-foreground">
            misia.ai
          </span>
        )}
      </Link>

      {!isMobile ? (
        <button
          onClick={toggleSidebar}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          className={
            isCollapsed
              ? "flex h-9 w-9 items-center justify-center rounded-lg text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-all"
              : "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-all"
          }
        >
          {isCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>
      ) : (
        <button
          onClick={toggleSidebar}
          aria-label="Close navigation"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sidebar-foreground/70 transition-all hover:bg-sidebar-accent hover:text-sidebar-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  </SidebarHeader>

      <SidebarContent className="px-2">
        <ScrollArea
          className="min-h-0 flex-1"
          scrollbarClassName={isCollapsed || isMobile ? "hidden" : undefined}
        >
          <SidebarGroup>
            <SidebarGroupLabel className="text-sidebar-foreground/50 text-xs uppercase tracking-wider px-3">
              {(!isCollapsed || isMobile) && "Platform"}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {mainNavItems.map((item) => {
                  const isInitiativesItem = item.url === "/initiatives";

                  if (isInitiativesItem) {
                    return (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton asChild tooltip={item.title} isActive={isInitiativesWorkspace}>
                          <Link
                            to={initiativesDefaultUrl}
                            onClick={handleInitiativesNavClick}
                            className={cn(
                              "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sidebar-foreground/80 transition-all hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                              isInitiativesWorkspace && "bg-sidebar-accent text-sidebar-primary font-medium",
                            )}
                          >
                            <item.icon className="h-5 w-5 shrink-0" />
                            {(!isCollapsed || isMobile) && (
                              <>
                                <span className="min-w-0 flex-1 truncate">{item.title}</span>
                                <ChevronRight
                                  className={cn(
                                    "h-4 w-4 shrink-0 transition-transform",
                                    isInitiativesNavOpen && "rotate-90",
                                  )}
                                />
                              </>
                            )}
                          </Link>
                        </SidebarMenuButton>

                        {isInitiativesNavOpen && (!isCollapsed || isMobile) ? (
                          <SidebarMenuSub>
                            {canAccessIdeas ? (
                              <SidebarMenuSubItem>
                                <SidebarMenuSubButton asChild isActive={location.pathname.startsWith("/ideas")}>
                                  <Link to="/ideas">
                                    <MessageSquarePlus className="h-4 w-4" />
                                    <span>Ideas</span>
                                  </Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ) : null}
                            {canAccessInitiatives ? (
                              <SidebarMenuSubItem>
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={
                                    location.pathname.startsWith("/initiatives") ||
                                    location.pathname.startsWith("/use-cases")
                                  }
                                >
                                  <Link to="/initiatives">
                                    <Lightbulb className="h-4 w-4" />
                                    <span>Initiatives</span>
                                  </Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ) : null}
                            {canAccessAILeads ? (
                              <SidebarMenuSubItem>
                                <SidebarMenuSubButton asChild isActive={location.pathname.startsWith("/ai-leads")}>
                                  <Link to="/ai-leads">
                                    <Sparkles className="h-4 w-4" />
                                    <span>AI Leads</span>
                                  </Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ) : null}
                          </SidebarMenuSub>
                        ) : null}
                      </SidebarMenuItem>
                    );
                  }

                  const isActiveByPrefix = item.activePrefix
                    ? location.pathname === item.activePrefix ||
                      location.pathname.startsWith(`${item.activePrefix}/`)
                    : false;

                  if (item.activePrefix) {
                    return (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton asChild tooltip={item.title} isActive={isActiveByPrefix}>
                          <Link
                            to={item.url}
                            className={cn(
                              "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sidebar-foreground/80 transition-all hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                              isActiveByPrefix && "bg-sidebar-accent text-sidebar-primary font-medium",
                            )}
                          >
                            <item.icon className="h-5 w-5 shrink-0" />
                            {(!isCollapsed || isMobile) && <span>{item.title}</span>}
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  }

                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild tooltip={item.title}>
                        <NavLink
                          to={item.url}
                          end={item.url === "/" || item.url === "/home"}
                          className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sidebar-foreground/80 transition-all hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                          activeClassName="bg-sidebar-accent text-sidebar-primary font-medium"
                        >
                          <item.icon className="h-5 w-5 shrink-0" />
                          {(!isCollapsed || isMobile) && <span>{item.title}</span>}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {canAccessAdmin && (
            // Single "Administration" entry. The card-based
            // AdminLanding page (/admin) is the canonical entry point;
            // sub-pages are reached via cards.
            <SidebarGroup className="mt-auto">
              <SidebarGroupLabel className="text-sidebar-foreground/50 text-xs uppercase tracking-wider px-3">
                {(!isCollapsed || isMobile) && "Platform Administration"}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild tooltip="Administration">
                      <NavLink
                        to="/admin"
                        className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sidebar-foreground/80 transition-all hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                        activeClassName="bg-sidebar-accent text-sidebar-primary font-medium"
                      >
                        <Shield className="h-5 w-5 shrink-0" />
                        {(!isCollapsed || isMobile) && <span>Administration</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}

          <SidebarGroup className={canAccessAdmin ? "" : "mt-auto"}>
            <SidebarGroupLabel className="text-sidebar-foreground/50 text-xs uppercase tracking-wider px-3">
              {(!isCollapsed || isMobile) && "Help & Support"}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {settingsItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild tooltip={item.title}>
                      <NavLink
                        to={item.url}
                        className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sidebar-foreground/80 transition-all hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                        activeClassName="bg-sidebar-accent text-sidebar-primary font-medium"
                      >
                        <item.icon className="h-5 w-5 shrink-0" />
                        {(!isCollapsed || isMobile) && <span>{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </ScrollArea>
      </SidebarContent>
      {!isMobile ? (
        <SidebarFooter className="shrink-0 border-t border-sidebar-border/50 p-2">
          <SidebarUserWidget />
        </SidebarFooter>
      ) : null}
    </Sidebar>
  );
}
